import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Drawer, IconButton, Tab, Tabs, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import User, { UserStatus, EmploymentType } from "../../interfaces/user";
import FieldItem from "../baseComponents/FieldItem";
import KYCDetails, { KYCDetailsStatus } from "../../interfaces/kycDetails";
import RegistrationService from "../../services/registrationService";
import RegistrationDocument, { RegistrationDocumentStatus } from "../../interfaces/registrationDocument";
import Loader from "../baseComponents/loader";
import { commentTextPattern } from "../../constants/patterns";
import ActionConfirmationModal from "../modals/actionConfirmationModal";
import { LINK } from "../../constants/constants";
import RoleManagement from "../../security/roleManagement";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { decodedJWT, getOwnerType, getUserRole } from "../../utils/commonUtils";
import UpdateUserModal from "../modals/updateUserModal";
import UserService from "../../services/userService";
import DropDown from "../baseComponents/dropdown";
import { closeSnackbar, enqueueSnackbar, SnackbarKey, SnackbarProvider } from "notistack";
import Coverage from "../../interfaces/coverage";
import CoverageService from "../../services/coverageService";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import getCoverageColDef from "../../colDef/coverageColumnDef";
import { createFilters } from "../../utils/filterUtils";
import Picklist from "../../interfaces/picklist";
import Filter from "../../interfaces/filters";
import { CoverageStatus } from "../../interfaces/coverage";

const allowedDocNames = [
    'aadhar_front',
    'aadhar_back',
    'pan',
    'profile_image',
    'cancelled_cheque_or_passbook_front',
    'vehicle_image',
];

interface UserDetailsDrawerProps {
    selectedUser: User | undefined;
    setSelectedUser: (user: User | undefined) => void;
    setUserInGrid: (user: User) => void;
    reloadFunction: () => void;
}

const drawerWidth = '92vw';

const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
    selectedUser, setSelectedUser, setUserInGrid, reloadFunction
}) => {
    const token: false | JWTTokenPayload = decodedJWT();
    const ownerType = getOwnerType(token);
    const userRole = getUserRole(token);
    const [open, setOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updatedUserStatus, setUpdatedUserStatus] = useState<string>('');
    const [updatedEmploymentType, setUpdatedEmploymentType] = useState<EmploymentType | null>(null);
    const [kycDetails, setKycDetails] = useState<KYCDetails | undefined>(undefined);
    const [kycDetailsCommentAndStatus, setKycDetailsCommentAndStatus] = useState<{ comment: string, status: string }>({ comment: '', status: '' });
    const [registrationDocuments, setRegistrationDocuments] = useState<RegistrationDocument[]>([]);
    const [registrationDocumentCommentsAndStatuses, setRegistrationDocumentCommentsAndStatuses] = useState<Record<string, { comment: string, status: string }>>({});
    const [userStatusConfirmationModalVisible, setUserStatusConfirmationModalVisible] = useState<boolean>(false);
    const [updateModalVisible, setUpdateModalVisible] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [coverageData, setCoverageData] = useState<Coverage[]>([]);
    const [coverageLoading, setCoverageLoading] = useState<boolean>(false);

    const showSnackbar = (msg: string, isSuccess: boolean) => {
        enqueueSnackbar(msg, {
            variant: isSuccess ? "success" : "error"
        });
    };

    const updateUserStatus = async () => {
        try {
            setIsLoading(true);
            if (!updatedUserStatus) {
                throw new Error('Please select a status');
            } else if (updatedUserStatus === UserStatus.APPROVED) {
                let errorStrValues: string[] = [];

                if (!updatedEmploymentType) {
                    throw new Error('Please set Employment Type before approving user');
                }
                if (!selectedUser || !selectedUser.employment_type) {
                    throw new Error('Please update Employment Type before approving user');
                }
                // Require saving any pending employment type change before approval.
                if (updatedEmploymentType !== selectedUser.employment_type) {
                    throw new Error('Please click Employment Type update button before approving user');
                }

                // 1. Check KYC Status
                if (kycDetailsCommentAndStatus.status !== KYCDetailsStatus.APPROVED) {
                    errorStrValues.push('KYC Details');
                }

                // 2. Check that all REQUIRED documents are present and approved
                allowedDocNames.forEach(requiredName => {
                    // Find the metadata for this required document name
                    const docMetadata = registrationDocuments.find(doc => doc.doc_name === requiredName);
                    
                    // Check if it exists in the status object and is approved
                    const statusEntry = docMetadata ? registrationDocumentCommentsAndStatuses[docMetadata.doc_id] : null;

                    if (!statusEntry || statusEntry.status !== RegistrationDocumentStatus.APPROVED) {
                        // If it's missing from the status object OR not approved, it's an error
                        errorStrValues.push(requiredName);
                    }
                });

                // Final Error Handling
                if (errorStrValues.length > 0) {
                    throw new Error('Please approve these details before approving user: ' + errorStrValues.join(', '));
                }
            }
            if (selectedUser) {
                const response = await UserService.updateUserStatus(
                    selectedUser.username, '', updatedUserStatus
                );
                if (response) {
                    showSnackbar('User Status Updated Successfully', true);
                    setSelectedUser(response);
                    setUserInGrid(response);
                }
            } else {
                throw new Error('Please select an user');
            }
        } catch (error) {
            showSnackbar(String(error), false);
        } finally {
            setIsLoading(false);
        }
    };

    const updateEmploymentType = async () => {
        try {
            setIsLoading(true);
            if (!selectedUser) {
                throw new Error('Please select an user');
            }
            if (!updatedEmploymentType) {
                throw new Error('Please select an Employment Type');
            }

            const response = await UserService.updateUserEmploymentType(
                selectedUser.username,
                updatedEmploymentType
            );
            if (response) {
                showSnackbar("Employment Type Updated Successfully", true);
                setSelectedUser(response);
                setUserInGrid(response);
            }
        } catch (error) {
            showSnackbar(String(error), false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchKYCDetails = async (username: string) => {
        const response = await RegistrationService.getKYCDetails(username);
        if (response) {
            setKycDetails(response);
            setKycDetailsCommentAndStatus({
                comment: response?.comment ?? '',
                status: response?.status ?? ''
            });
        }
    };

    const updateKYCStatus = async () => {
        try {
            setIsLoading(true);
            if (!kycDetailsCommentAndStatus.status) {
                throw new Error('Please select a status for KYC');
            }
            if (selectedUser) {
                const response = await RegistrationService.updateKYCStatus(
                    selectedUser.username, kycDetailsCommentAndStatus.status, kycDetailsCommentAndStatus.comment
                );
                if (response) {
                    showSnackbar('KYC Status Updated Successfully', true);
                    setKycDetails(response);
                    setKycDetailsCommentAndStatus({
                        comment: response?.comment ?? '',
                        status: response?.status ?? ''
                    });
                }
            } else {
                throw new Error('Please select an user');
            }
        } catch (error) {
            showSnackbar(String(error), false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRegistrationDocuments = async (username: string) => {
        const response = await RegistrationService.getRegistrationDocuments(username);
        if (response) {
            const finalResponse = (response as RegistrationDocument[]);
            let commentsAndStatuses: Record<string, { comment: string, status: string }> = {};
            finalResponse.forEach(val => {
                commentsAndStatuses[val.doc_id] = {
                    comment: val.comment ?? '',
                    status: val.status
                };
            });
            setRegistrationDocumentCommentsAndStatuses(commentsAndStatuses);
            setRegistrationDocuments(finalResponse);
        }
    };

    const updateRegistrationDocumentStatus = async (documentID: string) => {
        try {
            setIsLoading(true);
            if (!registrationDocumentCommentsAndStatuses[documentID].status) {
                throw new Error('Please select a status for Registration Document');
            }
            if (selectedUser) {
                const response = await RegistrationService.updateRegistrationDocumentStatus(
                    selectedUser.username,
                    documentID,
                    registrationDocumentCommentsAndStatuses[documentID].comment,
                    registrationDocumentCommentsAndStatuses[documentID].status
                );
                if (response) {
                    showSnackbar(
                        'Registration Document Status Updated Successfully for Document: ' + (response?.doc_name ?? ''),
                        true
                    );
                    const registrationDocIndex = registrationDocuments.findIndex(val => val.doc_id === documentID);
                    let registrationDocumentsArray = [...registrationDocuments];
                    registrationDocumentsArray[registrationDocIndex].status = response.status;
                    let registrationDocumentCommentsAndStatusesObject = { ...registrationDocumentCommentsAndStatuses };
                    registrationDocumentCommentsAndStatusesObject[response?.doc_name ?? ''] = {
                        comment: response?.comment ?? '',
                        status: response?.status ?? ''
                    };
                    setRegistrationDocuments(registrationDocumentsArray);
                    setRegistrationDocumentCommentsAndStatuses(registrationDocumentCommentsAndStatusesObject);
                }
            } else {
                throw new Error('Please select an user');
            }
        } catch (error) {
            showSnackbar(String(error), false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCoverageData = async (username: string) => {
        try {
            setCoverageLoading(true);
            // Create filters with username filter
            const statusPicklist = new Picklist(
                [],
                [CoverageStatus.PENDING_REVIEW, CoverageStatus.APPROVED, CoverageStatus.REJECTED, CoverageStatus.REMOVED],
                [],
                [],
                [],
                [],
                []
            );
            
            const filters = createFilters(
                ['status', 'username', 'pin_code', 'state', 'district'], 
                [],
                [],
                [],
                statusPicklist, 
                [],
                [],
                [],
                []
            );
            
            // Set username filter
            const usernameFilter = filters.find(f => f.id === 'username');
            if (usernameFilter) {
                usernameFilter.value = username;
            }
            
            const response = await CoverageService.getCoverageList(filters, 1, 100);
            if (response) {
                setCoverageData(response.coverage_entries);
            }
        } catch (error) {
            console.error('Error fetching coverage data:', error);
            setCoverageData([]);
        } finally {
            setCoverageLoading(false);
        }
    };

    const callAPIs = async (username: string) => {
        try {
            setIsLoading(true);
            await Promise.all([
                fetchKYCDetails(username),
                fetchRegistrationDocuments(username),
                fetchCoverageData(username)
            ]);
        } catch (_) {} finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setOpen(Boolean(selectedUser));
        if (selectedUser && RoleManagement.isAgency(ownerType)) {
            if (RoleManagement.isAdmin(userRole)) {
                if (RoleManagement.isAdmin(selectedUser.user_role)) {
                    alert('You are not authorized to view the details of this user');
                    setSelectedUser(undefined);
                } else {
                    setUpdatedUserStatus(selectedUser.status);
                    setUpdatedEmploymentType(selectedUser.employment_type ?? null);
                    callAPIs(selectedUser.username);
                }
            } else if (RoleManagement.isSupervisor(userRole)) {
                if (RoleManagement.isAdmin(selectedUser.user_role) || RoleManagement.isSupervisor(selectedUser.user_role)) {
                    alert('You are not authorized to view the details of this user');
                    setSelectedUser(undefined);
                } else {
                    setUpdatedUserStatus(selectedUser.status);
                    setUpdatedEmploymentType(selectedUser.employment_type ?? null);
                    callAPIs(selectedUser.username);
                }
            }
        }
    }, [selectedUser]);

    const handleClose = () => {
        setUpdatedUserStatus('');
        setUpdatedEmploymentType(null);
        setKycDetails(undefined);
        setKycDetailsCommentAndStatus({
            comment: '',
            status: ''
        });
        setRegistrationDocuments([]);
        setRegistrationDocumentCommentsAndStatuses({});
        setSelectedUser(undefined);
        setOpen(false);
        setSelectedTab(0);
        setCoverageData([]);
    };

    const setKycDetailsComment = (comment: string) => {
        setKycDetailsCommentAndStatus({
            ...kycDetailsCommentAndStatus,
            comment
        });
    };

    const setKycDetailsStatus = (status: string) => {
        setKycDetailsCommentAndStatus({
            ...kycDetailsCommentAndStatus,
            status
        });
    };

    const setRegistrationDocumentComment = (documentID: string, comment: string) => {
        let commentsAndStatusesObject = { ...registrationDocumentCommentsAndStatuses };
        commentsAndStatusesObject[documentID].comment = comment;
        setRegistrationDocumentCommentsAndStatuses(commentsAndStatusesObject);
    };

    const setRegistrationDocumentStatus = (documentID: string, status: string) => {
        let commentsAndStatusesObject = { ...registrationDocumentCommentsAndStatuses };
        commentsAndStatusesObject[documentID].status = status;
        setRegistrationDocumentCommentsAndStatuses(commentsAndStatusesObject);
    };

    const closeButton = (snackBarId: SnackbarKey) => {
        return (
            <IconButton onClick={() => closeSnackbar(snackBarId)}>
                <CloseIcon sx={{ color: '#ffffff' }} />
            </IconButton>
        );
    };

    return (
        <SnackbarProvider
            maxSnack={1}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            action={closeButton}
        >
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                open={open}
                onClose={handleClose}
                anchor="right"
            >
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                            <span style={{ color: '#888' }}>Username:</span> {selectedUser?.username}
                        </Typography>
                        <Tooltip title="Copy Username">
                            <IconButton
                                sx={{ ml: 1 }}
                                size="small"
                                onClick={() => navigator.clipboard.writeText(selectedUser?.username || '')}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ ml: 2 }}
                            onClick={() => setUpdateModalVisible(true)}
                        >
                            <Typography>Update {RoleManagement.isSuperAdmin(userRole) ? 'Admin' : 'User'}</Typography>
                        </Button>
                    </Box>
                    <IconButton onClick={handleClose} aria-label="close drawer" >
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
                <Divider />
                {isLoading ? (
                    <Loader color="primary" />
                ) : (
                    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab label="User Details" />
                            <Tab label="KYC Details" />
                            <Tab label="Registration Documents" />
                            <Tab label="Coverage" />
                        </Tabs>
                        {selectedTab === 0 && (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <Box sx={{ p: 3 }}>
                                <Box sx={{ p: 1, border: '1px solid lightGray', borderRadius: 2 }}>
                                    <FieldItem fieldName="Full Name" value={selectedUser?.name ?? ''} />
                                    <FieldItem fieldName="Email" value={selectedUser?.email ?? 'N/A'} />
                                    <FieldItem fieldName="Address" value={selectedUser?.address ?? ''} />
                                    <FieldItem fieldName="User Role" value={selectedUser?.user_role ?? ''} />
                                    <FieldItem fieldName="Entity Name" value={selectedUser?.owner_name ?? ''} />
                                    <FieldItem fieldName="Entity Type" value={selectedUser?.owner_type ?? ''} />
                                    <FieldItem fieldName="Employment Type" value={selectedUser?.employment_type ?? 'N/A'} />
                                    <FieldItem fieldName="Last Password Updated On" value={selectedUser?.last_password_updated_date ?? ''} />
                                </Box>
                                {RoleManagement.isAgency(ownerType) && (
                                    <Box>
                                        <Box display="flex">
                                            <DropDown
                                                label="Employment Type"
                                                selectedValue={updatedEmploymentType}
                                                onSelection={setUpdatedEmploymentType}
                                                items={Object.values(EmploymentType)}
                                                style={{ width: 225 }}
                                                height={40}
                                            />
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ ml: 2, height: 40, mt: 2 }}
                                                onClick={updateEmploymentType}
                                                disabled={!updatedEmploymentType || updatedEmploymentType === selectedUser?.employment_type}
                                            >
                                                <Typography>Update</Typography>
                                            </Button>
                                        </Box>
                                        <Box display="flex" mt={2}>
                                            <DropDown
                                                label="User Status"
                                                selectedValue={updatedUserStatus}
                                                onSelection={setUpdatedUserStatus}
                                                items={Object.values(UserStatus)}
                                                style={{ width: 225 }}
                                                height={40}
                                            />
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ ml: 2, height: 40, mt: 2 }}
                                                onClick={() => setUserStatusConfirmationModalVisible(true)}
                                                disabled={updatedUserStatus === (selectedUser?.status ?? '')}
                                            >
                                                <Typography>Update</Typography>
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        )}
                        {selectedTab === 1 && (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <Box sx={{ p: 3 }}>
                                {kycDetails ? (
                                    <>
                                        <Box sx={{ p: 1, border: '1px solid lightGray', borderRadius: 2, mb: 2 }}>
                                            <FieldItem fieldName="PAN Number" value={kycDetails?.pan ?? ''} />
                                            <FieldItem fieldName="Aadhar Number" value={kycDetails?.aadhar_number ?? ''} />
                                            <FieldItem fieldName="Permanent Address" value={kycDetails?.permanent_address ?? ''} />
                                        </Box>
                                        <TextField
                                            fullWidth
                                            value={kycDetailsCommentAndStatus.comment}
                                            onChange={e => setKycDetailsComment(e.target.value.replace(commentTextPattern, ""))}
                                            rows={3}
                                            label="Comment"
                                            type="text"
                                            multiline
                                        />
                                        <Box display="flex">
                                            <DropDown
                                                label="KYC Status"
                                                selectedValue={kycDetailsCommentAndStatus.status}
                                                onSelection={setKycDetailsStatus}
                                                items={Object.values(KYCDetailsStatus)}
                                                style={{ width: 225 }}
                                                height={40}
                                            />
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ ml: 2, height: 40, mt: 2 }}
                                                onClick={updateKYCStatus}
                                                disabled={kycDetailsCommentAndStatus.status === (kycDetails?.status ?? '')}
                                            >
                                                <Typography>Update</Typography>
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Typography>No KYC Details uploaded by user</Typography>
                                )}
                            </Box>
                        </Box>
                        )}
                        {selectedTab === 2 && (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <Box sx={{ p: 3 }}>
                                {registrationDocuments.map((registrationDocument: RegistrationDocument, index: number) => {
                                    return (
                                        <Box key={index}>
                                            <Box sx={{ p: 1, border: '1px solid lightGray', borderRadius: 2, mb: 2 }}>
                                                <FieldItem fieldName="Document Name" value={registrationDocument.doc_name} />
                                                <FieldItem fieldName="Uploaded At" value={registrationDocument.uploaded_at} />
                                                <FieldItem fieldName="File" value={registrationDocument.signed_url ?? ''} fieldType={LINK} />
                                            </Box>
                                            <TextField
                                                fullWidth
                                                value={registrationDocumentCommentsAndStatuses[registrationDocument.doc_id].comment}
                                                onChange={e => setRegistrationDocumentComment(registrationDocument.doc_id, e.target.value.replace(commentTextPattern, ""))}
                                                rows={3}
                                                label="Comment"
                                                type="text"
                                                multiline
                                            />
                                            <Box display="flex">
                                                <DropDown
                                                    label="Document Status"
                                                    selectedValue={registrationDocumentCommentsAndStatuses[registrationDocument.doc_id].status}
                                                    onSelection={val => setRegistrationDocumentStatus(registrationDocument.doc_id, val)}
                                                    items={Object.values(RegistrationDocumentStatus)}
                                                    style={{ width: 225 }}
                                                    height={40}
                                                />
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    sx={{ ml: 2, height: 40, mt: 2 }}
                                                    onClick={() => updateRegistrationDocumentStatus(registrationDocument.doc_id)}
                                                    disabled={registrationDocumentCommentsAndStatuses[registrationDocument.doc_id].status === registrationDocument.status}
                                                >
                                                    <Typography>Update</Typography>
                                                </Button>
                                            </Box>
                                            {index < registrationDocuments.length - 1 && (
                                                <Divider sx={{ mt: 2, mb: 2 }} />
                                            )}
                                        </Box>
                                    );
                                })}
                                {registrationDocuments.length === 0 && (
                                    <Typography>No Documents uploaded by user</Typography>
                                )}
                            </Box>
                        </Box>
                        )}
                        {selectedTab === 3 && (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <Box sx={{ p: 3 }}>
                                {coverageLoading ? (
                                    <Loader color="primary" />
                                ) : (
                                    <>
                                        {coverageData.length > 0 ? (
                                            <SearchResultGrid
                                                getRowId={(row: Coverage) => row.id}
                                                results={coverageData}
                                                rowCount={coverageData.length}
                                                onSelectRow={() => {}}
                                                columns={getCoverageColDef()}
                                                isRowSelectionAllowed={false}
                                                hideFooter={true}
                                            />
                                        ) : (
                                            <Typography>No coverage data found for this user</Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                        )}
                    </Box>
                )}
                <ActionConfirmationModal
                    visible={userStatusConfirmationModalVisible} 
                    onClose={() => setUserStatusConfirmationModalVisible(false)}
                    onConfirm={updateUserStatus}
                    headerText={`User Status update for ${selectedUser?.username}`}
                    bodyText={`Are you sure you want to update the status from ${selectedUser?.status} to ${updatedUserStatus}?`}
                />
                {selectedUser && (
                    <UpdateUserModal
                        handleCloseModal={() => setUpdateModalVisible(false)}
                        userToUpdate={selectedUser}
                        openModal={updateModalVisible}
                        reloadFunction={reloadFunction}
                    />
                )}
            </Drawer>
        </SnackbarProvider>
    );
};

export default UserDetailsDrawer;